import { useState } from 'preact/hooks';
import { ApiError } from '../lib/api.js';
import { login, register } from '../lib/engine.js';

const ERRORS: Record<string, string> = {
  invalid_credentials: 'Неверное имя или пароль',
  invite_required: 'Нужен код приглашения',
  invite_invalid: 'Код недействителен или уже использован',
  invite_expired: 'Срок действия кода истёк',
  username_taken: 'Это имя уже занято',
  network_unreachable: 'Сервер недоступен. Проверь соединение',
  bad_request: 'Проверь правильность заполнения полей',
};

export function Auth() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: Event) => {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      if (mode === 'login') {
        await login(username.trim().toLowerCase(), password);
      } else {
        await register({
          username: username.trim().toLowerCase(),
          displayName: displayName.trim() || username.trim(),
          password,
          ...(inviteCode.trim() ? { inviteCode: inviteCode.trim().toUpperCase() } : {}),
        });
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        setError(ERRORS[caught.code] ?? `Ошибка: ${caught.code}`);
      } else {
        setError(caught instanceof Error ? caught.message : 'Что-то пошло не так');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="screen">
      <div class="scroll">
        <form class="form" onSubmit={submit}>
          <h1>{mode === 'login' ? 'Вход' : 'Регистрация'}</h1>

          <label>
            Имя пользователя
            <input
              value={username}
              onInput={(event) => setUsername((event.target as HTMLInputElement).value)}
              autocomplete="username"
              autocapitalize="none"
              spellcheck={false}
              required
              minLength={3}
              maxLength={32}
              pattern="[a-zA-Z0-9_]+"
            />
          </label>

          {mode === 'register' && (
            <label>
              Отображаемое имя
              <input
                value={displayName}
                onInput={(event) => setDisplayName((event.target as HTMLInputElement).value)}
                maxLength={64}
                placeholder="как тебя увидят собеседники"
              />
            </label>
          )}

          <label>
            Пароль
            <input
              type="password"
              value={password}
              onInput={(event) => setPassword((event.target as HTMLInputElement).value)}
              autocomplete={mode === 'login' ? 'current-password' : 'new-password'}
              required
              minLength={8}
            />
          </label>

          {mode === 'register' && (
            <label>
              Код приглашения
              <input
                value={inviteCode}
                onInput={(event) => setInviteCode((event.target as HTMLInputElement).value)}
                autocapitalize="characters"
                spellcheck={false}
                placeholder="XXXX-XXXX-XXXX-XXXX"
              />
            </label>
          )}

          {error && <div class="error">{error}</div>}

          <button class="btn" type="submit" disabled={busy}>
            {busy ? 'Минуту…' : mode === 'login' ? 'Войти' : 'Создать аккаунт'}
          </button>

          <button
            class="btn ghost"
            type="button"
            onClick={() => {
              setMode(mode === 'login' ? 'register' : 'login');
              setError(null);
            }}
          >
            {mode === 'login' ? 'У меня есть код приглашения' : 'Уже есть аккаунт'}
          </button>

          {mode === 'register' && (
            <div class="notice">
              <strong>Пароль восстановить нельзя.</strong> Из него выводится ключ, которым
              зашифрован твой приватный ключ — на сервере лежит только результат, и подобрать
              исходный пароль по нему нельзя. Забудешь пароль — переписка не расшифруется
              ни тобой, ни кем-либо ещё.
            </div>
          )}

          {busy && mode !== 'login' && (
            <div class="notice">
              Вывод ключа из пароля занимает несколько секунд — так и задумано: это делает
              перебор пароля дорогим.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
